import {FIELD_TYPES} from '@api/constants';

export const CHECKBOX_GROUP_ERROR_TYPES = {
    MINIMUM: 'checkboxGroupMinimum',
    MAXIMUM: 'checkboxGroupMaximum',
    RANGE: 'checkboxGroupRange',
    EXACTLY: 'checkboxGroupExactly'
} as const;

export type CheckboxGroupErrorType = typeof CHECKBOX_GROUP_ERROR_TYPES[keyof typeof CHECKBOX_GROUP_ERROR_TYPES];

export function getIsCheckboxGroupErrorType(type?: string | null): boolean {
    return Object.values(CHECKBOX_GROUP_ERROR_TYPES).includes(type as CheckboxGroupErrorType);
}

export const CHECKBOX_GROUP_VIOLATIONS = {
    BELOW_MINIMUM: 'below_min',
    ABOVE_MAXIMUM: 'above_max'
} as const;

export type CheckboxGroupViolation = typeof CHECKBOX_GROUP_VIOLATIONS[keyof typeof CHECKBOX_GROUP_VIOLATIONS];

/**
 * Structural shape of a checkbox field, satisfied both by the REST FieldView
 * and by the looser field objects used in signing validation.
 */
export interface CheckboxGroupField {
    id: string;
    approval_id?: string;
    type?: string;
    value?: string | boolean;
    group?: string;
    minimum_required?: number | null;
    maximum_required?: number | null;
}

export interface CheckboxGroupState {
    key: string;
    approvalId: string;
    groupName: string;
    fieldIds: string[];
    groupSize: number;
    effectiveMin: number | null;
    effectiveMax: number | null;
    checkedCount: number;
    isSatisfied: boolean;
    violation: CheckboxGroupViolation | null;
}

export type CheckboxGroupsMap = Map<string, CheckboxGroupState>;

interface CheckboxGroupBucket {
    approvalId: string;
    groupName: string;
    fieldIds: string[];
    minimums: number[];
    maximums: number[];
    checkedCount: number;
}

function getIsCheckboxField(field: CheckboxGroupField): boolean {
    return field.type?.toLowerCase() === FIELD_TYPES.CHECKBOX;
}

/**
 * A minimum of zero (or below) carries no constraint, so it is discarded.
 */
function getIsUsableMinimum(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/**
 * A maximum of zero is a valid "none may be selected" constraint.
 */
function getIsUsableMaximum(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

export function getCheckboxGroupKey(field: CheckboxGroupField): string {
    return `${field.approval_id}::${field.group}`;
}

export function getIsCheckboxChecked(field: CheckboxGroupField): boolean {
    return field.value === true || field.value === 'true';
}

/**
 * Aggregating minimum and maximum from different fields of one group can yield
 * minimum > maximum, which no selection could satisfy; the minimum gives way.
 */
function getGroupStateFromBucket(key: string, bucket: CheckboxGroupBucket): CheckboxGroupState {
    const groupSize = bucket.fieldIds.length;

    const effectiveMax = bucket.maximums.length > 0
        ? Math.min(Math.min(...bucket.maximums), groupSize)
        : null;

    let effectiveMin = bucket.minimums.length > 0
        ? Math.min(Math.max(...bucket.minimums), groupSize)
        : null;

    if (effectiveMin !== null && effectiveMax !== null && effectiveMin > effectiveMax) {
        effectiveMin = effectiveMax;
    }

    const {checkedCount} = bucket;
    const isBelowMinimum = effectiveMin !== null && checkedCount < effectiveMin;
    const isAboveMaximum = effectiveMax !== null && checkedCount > effectiveMax;

    let violation: CheckboxGroupViolation | null = null;
    if (isBelowMinimum) {
        violation = CHECKBOX_GROUP_VIOLATIONS.BELOW_MINIMUM;
    } else if (isAboveMaximum) {
        violation = CHECKBOX_GROUP_VIOLATIONS.ABOVE_MAXIMUM;
    }

    return {
        key,
        approvalId: bucket.approvalId,
        groupName: bucket.groupName,
        fieldIds: bucket.fieldIds,
        groupSize,
        effectiveMin,
        effectiveMax,
        checkedCount,
        isSatisfied: violation === null,
        violation
    };
}

/**
 * Groups checkbox fields by approval and group name.
 * Minimum and maximum may differ between fields of the same group, so the strictest
 * value wins, and both are clamped to the group size to keep the group completable.
 */
export function getCheckboxGroupsMap(fields: CheckboxGroupField[] = []): CheckboxGroupsMap {
    const buckets = new Map<string, CheckboxGroupBucket>();

    for (const field of fields) {
        if (!getIsCheckboxField(field) || !field.group || !field.approval_id) {
            continue;
        }

        const key = getCheckboxGroupKey(field);
        let bucket = buckets.get(key);

        if (!bucket) {
            bucket = {
                approvalId: field.approval_id,
                groupName: field.group,
                fieldIds: [],
                minimums: [],
                maximums: [],
                checkedCount: 0
            };
            buckets.set(key, bucket);
        }

        bucket.fieldIds.push(field.id);

        if (getIsUsableMinimum(field.minimum_required)) {
            bucket.minimums.push(field.minimum_required);
        }

        if (getIsUsableMaximum(field.maximum_required)) {
            bucket.maximums.push(field.maximum_required);
        }

        if (getIsCheckboxChecked(field)) {
            bucket.checkedCount += 1;
        }
    }

    const groups: CheckboxGroupsMap = new Map();

    buckets.forEach((bucket, key) => {
        groups.set(key, getGroupStateFromBucket(key, bucket));
    });

    return groups;
}

/**
 * Only groups carrying a minimum or a maximum constraint. Checkbox groups without
 * constraints keep behaving as independent fields.
 */
export function getConstrainedCheckboxGroups(fields: CheckboxGroupField[] = []): CheckboxGroupsMap {
    const constrainedGroups: CheckboxGroupsMap = new Map();

    getCheckboxGroupsMap(fields).forEach((group, key) => {
        if (group.effectiveMin !== null || group.effectiveMax !== null) {
            constrainedGroups.set(key, group);
        }
    });

    return constrainedGroups;
}

/**
 * A group demands something from the signer when it asks for a minimum, or when the
 * current selection already breaks its limits. A maximum-only group nobody exceeded
 * asks for nothing, so counting it as a required action would inflate the progress
 * counter with an action that does not exist.
 */
export function getIsCheckboxGroupRequiringAction(group: CheckboxGroupState): boolean {
    return (group.effectiveMin !== null && group.effectiveMin > 0) || group.violation !== null;
}

export function getCheckboxGroupsRequiringAction(fields: CheckboxGroupField[] = []): CheckboxGroupsMap {
    const groupsRequiringAction: CheckboxGroupsMap = new Map();

    getConstrainedCheckboxGroups(fields).forEach((group, key) => {
        if (getIsCheckboxGroupRequiringAction(group)) {
            groupsRequiringAction.set(key, group);
        }
    });

    return groupsRequiringAction;
}

export function getAreConstrainedCheckboxGroupsSatisfied(groups: CheckboxGroupsMap): boolean {
    for (const group of groups.values()) {
        if (!group.isSatisfied) {
            return false;
        }
    }

    return true;
}

export function getIsFieldInConstrainedCheckboxGroup(
    field: CheckboxGroupField,
    constrainedGroups: CheckboxGroupsMap
): boolean {
    if (!getIsCheckboxField(field) || !field.group || !field.approval_id) {
        return false;
    }

    return constrainedGroups.has(getCheckboxGroupKey(field));
}

export function getCheckboxGroupErrorType(group: CheckboxGroupState): CheckboxGroupErrorType | null {
    if (group.isSatisfied) {
        return null;
    }

    const {effectiveMin, effectiveMax} = group;

    if (effectiveMin !== null && effectiveMax !== null) {
        return effectiveMin === effectiveMax
            ? CHECKBOX_GROUP_ERROR_TYPES.EXACTLY
            : CHECKBOX_GROUP_ERROR_TYPES.RANGE;
    }

    return effectiveMin !== null
        ? CHECKBOX_GROUP_ERROR_TYPES.MINIMUM
        : CHECKBOX_GROUP_ERROR_TYPES.MAXIMUM;
}

/**
 * Placeholder values for the checkbox group validation messages. Always the clamped
 * numbers, so the signer is never asked for more options than the group holds.
 */
export function getCheckboxGroupMessageValues(group: CheckboxGroupState): Record<string, number> {
    const {effectiveMin, effectiveMax} = group;

    if (effectiveMin !== null && effectiveMax !== null) {
        return effectiveMin === effectiveMax
            ? {exactlyRequired: effectiveMin}
            : {minimumRequired: effectiveMin, maximumRequired: effectiveMax};
    }

    if (effectiveMin !== null) {
        return {minimumRequired: effectiveMin};
    }

    return {maximumRequired: effectiveMax as number};
}
